import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/database.generated';
import { fetchAllRows } from '../utils/core/fetchAllRows';
import { logger } from '../utils/core/logger';

interface WorkspaceAnalytics {
    login_urls?: string[];
    posthog_url?: string;
}

export type WorkspaceStatus = 'draft' | 'live' | 'disabled';

export interface Workspace {
    /** `public.workspaces.id`: the key of everything a workspace owns (knowledge, FAQs, datasets…). */
    id: string;
    /**
     * Registered (non-alias) domain of the canonical root's host. Hostname-keyed readers
     * (analytics, tracking, integrations) and access grants use it, so two workspaces on
     * one host — or on a host and its alias — share those numbers by design.
     */
    domain: string;
    /** Host of the canonical root as placed, e.g. `www.example.com`. For display. */
    host: string;
    /** Path of the canonical root; `/` when the workspace owns the whole host. */
    pathPrefix: string;
    /** Configuration handle. Equals `domain` for a workspace at a bare host. */
    configKey: string;
    status: WorkspaceStatus;
    name: string;
    analytics?: WorkspaceAnalytics | null;
}

/** A workspace placed under a path shares its host with the workspace at `/`. */
export function isPathWorkspace(workspace: Pick<Workspace, 'pathPrefix'>): boolean {
    return workspace.pathPrefix !== '/';
}

/** Where the workspace lives, as shown next to its name: `example.com` or `example.com/fr`. */
export function workspaceLocation(
    workspace: Pick<Workspace, 'domain' | 'host' | 'pathPrefix'>
): string {
    // A bare-host workspace keeps showing its registered domain, as the selector always has.
    return isPathWorkspace(workspace)
        ? `${workspace.host}${workspace.pathPrefix}`
        : workspace.domain;
}

interface WorkspaceRow {
    id: string;
    config_key: string;
    name: string;
    status: string;
    clients: { client_type: string | null } | null;
    workspace_roots: { host: string; path_prefix: string; is_canonical: boolean }[] | null;
}

/**
 * Fetch the workspaces the signed-in user can select, through that user's client.
 *
 * RLS on the base tables (`has_site_access`) already limits the rows to the caller's
 * workspaces. A workspace without a canonical root is skipped: it has no hostname for
 * the hostname-keyed screens to read.
 */
export async function fetchWorkspaces(supabase: SupabaseClient<Database>): Promise<Workspace[]> {
    const workspaceRows = await fetchAllRows((from, to) =>
        supabase
            .from('workspaces')
            .select(
                'id, config_key, name, status, clients!sites_client_id_fkey(client_type), workspace_roots(host, path_prefix, is_canonical)'
            )
            .order('config_key')
            .range(from, to)
            // `workspaces` / `workspace_roots` are views over `sites` / `site_roots`
            // (IX-5063). Postgres reports every view column as nullable, so the
            // generated types lose the base tables' NOT NULLs; restore them here.
            .overrideTypes<WorkspaceRow[], { merge: false }>()
    );

    // Domain rows carry the selector label of a bare-host workspace, and map an alias
    // host (`www.example.com`) to the registered domain its data is keyed by.
    const domainRows = await fetchAllRows((from, to) =>
        supabase
            .from('domains')
            .select('domain, canonical_domain, workspace_name')
            .order('domain')
            .range(from, to)
    );
    const workspaceNames = new Map(domainRows.map((d) => [d.domain, d.workspace_name]));
    const registeredDomains = new Map(
        domainRows.map((d) => [d.domain, d.canonical_domain ?? d.domain])
    );

    // Video clients are throwaway recording fixtures, not workspaces anyone picks.
    const selectable = workspaceRows.flatMap((w) => {
        const canonical = w.workspace_roots?.find((r) => r.is_canonical);
        return w.clients?.client_type !== 'video' && canonical ? [{ ...w, canonical }] : [];
    });

    if (selectable.length === 0) {
        return [];
    }

    // Fetch identity configs (company_name for display)
    let identityConfigs: { domain: string; config: unknown }[] = [];
    try {
        identityConfigs = await fetchAllRows((from, to) =>
            supabase
                .schema('config')
                .from('client_configs')
                .select('domain, config')
                .eq('config_slug', 'identity')
                .eq('config_scope', 'global')
                .order('domain')
                .range(from, to)
        );
    } catch (identityError) {
        logger.error('Error fetching identity configs:', identityError);
        // Non-fatal: continue without names
    }

    // Fetch analytics configs (posthog_url, login_urls)
    const { data: analyticsConfigs, error: analyticsError } = await supabase
        .schema('config')
        .from('client_configs')
        .select('domain, config')
        .eq('config_slug', 'analytics')
        .eq('config_scope', 'website');

    if (analyticsError) {
        logger.error('Error fetching analytics configs:', analyticsError);
        // Non-fatal: continue without analytics
    }

    // Build lookup maps
    const identityMap = new Map<string, Record<string, unknown>>();
    for (const ic of identityConfigs || []) {
        if (ic.config) identityMap.set(ic.domain, ic.config as Record<string, unknown>);
    }

    const analyticsMap = new Map<string, WorkspaceAnalytics>();
    for (const ac of analyticsConfigs || []) {
        const config = ac.config as Record<string, unknown> | null;
        if (config) {
            analyticsMap.set(ac.domain, {
                login_urls: config.login_urls as string[] | undefined,
                posthog_url: config.posthog_url as string | undefined,
            });
        }
    }

    // Label precedence at a bare host is unchanged: explicit workspace_name →
    // identity.company_name → domain. A path workspace has no domain row or identity
    // config of its own yet, so it shows the name staff gave it.
    return selectable.map((w) => {
        const identity = identityMap.get(w.config_key);
        const pathRooted = w.canonical.path_prefix !== '/';
        return {
            id: w.id,
            domain: registeredDomains.get(w.canonical.host) ?? w.canonical.host,
            host: w.canonical.host,
            pathPrefix: w.canonical.path_prefix,
            configKey: w.config_key,
            status: w.status as WorkspaceStatus,
            name: pathRooted
                ? w.name
                : workspaceNames.get(w.config_key) ||
                  (identity?.company_name as string) ||
                  w.canonical.host,
            analytics: analyticsMap.get(w.config_key) || null,
        };
    });
}
